import type { UmbEntityBulkAction } from './entity-bulk-action.interface.js';
import type { UmbEntityBulkActionElement } from './entity-bulk-action-element.interface.js';
import { html, customElement, property, when } from '@umbraco-cms/backoffice/external/lit';
import { UmbActionExecutedEvent } from '@umbraco-cms/backoffice/event';
import { UmbLitElement } from '@umbraco-cms/backoffice/lit-element';
import type {
	ManifestEntityBulkAction,
	MetaEntityBulkActionDefaultKind,
} from '@umbraco-cms/backoffice/extension-registry';

@customElement('umb-entity-bulk-action')
export class UmbEntityBulkActionDefaultElement<
	MetaType extends MetaEntityBulkActionDefaultKind = MetaEntityBulkActionDefaultKind,
	ApiType extends UmbEntityBulkAction<MetaType> = UmbEntityBulkAction<MetaType>,
>
	extends UmbLitElement
	implements UmbEntityBulkActionElement
{
	@property({ attribute: false })
	manifest?: ManifestEntityBulkAction<MetaType>;

	api?: ApiType;

	async #onClick(event: PointerEvent) {
		if (!this.api) return;
		event.stopPropagation();

		try {
			await this.api.execute();
		} catch (error) {
			// The action did not complete, so it must not be announced as executed: consumers treat that
			// event as "done" and tear down the selection — and with it this element — which would cut short
			// any notification the failure is still presenting.
			// A dismissed modal rejects with a reason rather than an Error; that is a cancellation, not a failure.
			if (error instanceof Error) {
				console.error('Error executing action:', error);
			}
			return;
		}

		this.dispatchEvent(new UmbActionExecutedEvent());
	}

	override render() {
		return html`
			<uui-button
				color="default"
				look="secondary"
				label=${this.localize.string(this.manifest?.meta.label ?? '')}
				@click=${this.#onClick}>
				${when(this.manifest?.meta.icon, () => html`<uui-icon name=${this.manifest!.meta.icon}></uui-icon>`)}
				<span>${this.localize.string(this.manifest?.meta.label ?? '')}</span>
			</uui-button>
		`;
	}
}

export default UmbEntityBulkActionDefaultElement;

declare global {
	interface HTMLElementTagNameMap {
		'umb-entity-bulk-action': UmbEntityBulkActionDefaultElement;
	}
}
